import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError, switchMap, tap } from 'rxjs/operators';
import { FLOORS_SERVICE } from '../../services/floors/floors.service.base';
import { IFloorsService } from '../../interfaces/services/floors-service.interface';
import { getErrorMessage } from '../../utils/error.utils';
import { ToastService } from '../../services/toast.service';
import * as FloorsActions from './floors.actions';

@Injectable()
export class FloorsEffects {
  private actions$ = inject(Actions);
  private floorsService = inject<IFloorsService>(FLOORS_SERVICE);
  private toastService = inject(ToastService);

  loadFloors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FloorsActions.loadFloors),
      switchMap(({ projectId }) =>
        this.floorsService.getFloors(projectId).pipe(
          map((floors) => FloorsActions.loadFloorsSuccess({ floors })),
          catchError((error) => of(FloorsActions.loadFloorsFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  addFloor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FloorsActions.addFloor),
      mergeMap(({ projectId, name }) =>
        this.floorsService.addFloor(projectId, name).pipe(
          map((floor) => FloorsActions.addFloorSuccess({ floor })),
          catchError((error) => of(FloorsActions.addFloorFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  deleteFloor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FloorsActions.deleteFloor),
      mergeMap(({ id }) =>
        this.floorsService.deleteFloor(id).pipe(
          map(() => FloorsActions.deleteFloorSuccess({ id })),
          catchError((error) => of(FloorsActions.deleteFloorFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  addFloorSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FloorsActions.addFloorSuccess),
        tap(({ floor }) => {
          this.toastService.success(`Floor "${floor.name}" added successfully.`, 'Success');
        })
      ),
    { dispatch: false }
  );

  addFloorFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FloorsActions.addFloorFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );

  deleteFloorFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FloorsActions.deleteFloorFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );
}
